import React from 'react';
import {
  ContextLessTrackingFunctions,
  TrackingEvent,
  TrackingFunctions,
} from '../types/tracking';

export type TrackingContextValue<Ctx> = {
  trackingCtxDataRef: React.MutableRefObject<Ctx | null>;
  trackingFunctions: ContextLessTrackingFunctions;
};

const TrackingContext = React.createContext<TrackingContextValue<unknown>>({
  trackingCtxDataRef: {current: null},
  trackingFunctions: {},
});

export interface TrackingContextProviderProps<Ctx> {
  ctxRef: React.MutableRefObject<Ctx | null>;
  trackingFunctions?: TrackingFunctions<Ctx>;
  children: React.ReactNode;
}
export function TrackingContextProvider<Ctx>({
  ctxRef,
  trackingFunctions,
  children,
}: TrackingContextProviderProps<Ctx>) {
  const internalTrackingFunctions = React.useMemo(() => {
    const internalFunctions: ContextLessTrackingFunctions = {};
    if (!trackingFunctions) {
      return internalFunctions;
    }

    for (const [key, track] of Object.entries(trackingFunctions)) {
      internalFunctions[key as unknown as TrackingEvent] = (payload) => {
        // The tracking context setter must have been used before the first tracking event.
        if (ctxRef.current === null) {
          throw new Error(
            'Tried to track an event without the tracking context data being initialized. This should never happen!'
          );
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- There is no good way I see to make typescript understand why this is fine
        // @ts-ignore
        track(payload, ctxRef.current);
      };
    }
    return internalFunctions;
  }, [trackingFunctions, ctxRef]);

  const trackingContextValue = React.useMemo(
    () => ({
      trackingCtxDataRef: ctxRef,
      trackingFunctions: internalTrackingFunctions,
    }),
    [ctxRef, internalTrackingFunctions]
  );

  return (
    <TrackingContext.Provider value={trackingContextValue}>
      {children}
    </TrackingContext.Provider>
  );
}

export function useTrackingContextSetter<CTX>(): (data: CTX) => void {
  const contextValue = React.useContext(TrackingContext);

  return React.useCallback(
    (data) => {
      contextValue.trackingCtxDataRef.current = data;
    },
    [contextValue]
  );
}

export function useTrackingFunctions(): ContextLessTrackingFunctions {
  return React.useContext(TrackingContext).trackingFunctions;
}
